import { useEffect, useRef } from 'react';
import { saveDraftToStorage, saveDraftToDatabase } from '../useClaimState';
import type { ClaimState } from '../types';

/**
 * Auto-save hook for new claim flow.
 * Handles both localStorage (immediate) and database (debounced) saves.
 */
export function useAutoSave(state: ClaimState) {
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Only auto-save if state is dirty and we're past the welcome/extracting phases
    if (state.isDirty && state.phase !== 'welcome' && state.phase !== 'extracting') {
      // Always save to localStorage immediately for instant recovery
      saveDraftToStorage(state);

      // Debounce database save (only when case exists)
      if (state.caseId) {
        if (autoSaveTimeoutRef.current) {
          clearTimeout(autoSaveTimeoutRef.current);
        }
        autoSaveTimeoutRef.current = setTimeout(async () => {
          try {
            await saveDraftToDatabase(state.caseId!, state);
          } catch (err) {
            console.error('Database auto-save failed:', err);
            // Non-fatal - localStorage has the data
          }
        }, 1000); // 1 second debounce
      }
    }

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [state]);
}
